import { useState, useCallback, useEffect, useRef } from 'react'
import styles from './CanvasDashboard.module.scss'
import { PlusIcon, ProjectorScreenIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopScreenLayout,
    ScoopText,
    ScoopSkeleton,
    ScoopRow,
    ScoopColumn,
    useToast,
} from '../../ui'
import { debounce } from 'lodash'
import { NewCanvasMenu } from './NewCanvasMenu/NewCanvasMenu'
import { useModal, MODALS } from '../../modals'
import { useDispatch, useSelector } from 'react-redux'
import { CanvasBox } from './CanvasBox/CanvasBox'
import { useLocation, useNavigate } from 'react-router-dom'
import { switchWorkspace } from '../../../store/actions/authActions'
import { setPresentationLoading, setShowRecipeModal } from '../../../store/actions/uiActions'
import { useCanvases } from '../../../api/hooks/canvases/useCanvases'
import { useWorkspaces } from '../../../api/hooks/workspaces/useWorkspaces'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { EmptyDataset } from '../../EmptyDataset/EmptyDataset'
import { useUserInfo } from '../../../api/hooks/user/useUserInfo'
import { getWorkspaceID } from '../../../utils'
import { ROUTES } from '../../../router/routes'
import { useInsights } from '../../../api/hooks/insights/useInsights'
import { HelpBotPromptButton } from '../../HelpBot/components/HelpBotPromptButton/HelpBotPromptButton'
import { HELP_BOT_PROMPTS } from '../../HelpBot/helpBotPrompts'
import { useIsTourWorkspace } from '../../../hooks/useIsTourWorkspace'

export const CanvasDashboard = () => {
    const personalWorkspace = useSelector((state) => state.auth.personalWorkspace)
    const isPolling = useSelector((state) => state.ui.isCanvasPolling)
    const dispatch = useDispatch()
    const deleteModal = useModal(MODALS.COMMON.DELETE)

    const location = useLocation()
    const { openCanvasModal } = location.state || false
    const [search, setSearch] = useState('')
    const [filteredCanvases, setFilteredCanvases] = useState([])
    const [openNewCanvas, setOpenNewCanvas] = useState(false)
    const toast = useToast()
    const navigate = useNavigate()
    const { canvases, deleteCanvas, refetch, isLoading } = useCanvases()
    const cavasesAmountRef = useRef(null)
    const { workspaces: workspacesAccessible } = useWorkspaces()
    const { user } = useUserInfo()
    const { workspaceMetadata, isLoading: workspaceMetadataLoading } = useWorkspaceMetadata()
    const { insights } = useInsights()
    const workspaceID = getWorkspaceID()
    const anchorEl = useRef(null)

    useEffect(() => {
        if (openCanvasModal && workspaceMetadata?.inboxes.length > 0) {
            setOpenNewCanvas(openCanvasModal)
        }
    }, [location.state])

    useEffect(() => {
        if (canvases && !isLoading) {
            if (canvases.canvasObjects) setFilteredCanvases(canvases.canvasObjects)
            else setFilteredCanvases(canvases)
        }
    }, [canvases, isLoading])

    useEffect(() => {
        if (isPolling) {
            const newCanvas = {
                canvasID: 'Placeholder',
                canvasName: 'AI Presentation',
                thumbnailURL: '',
                isGenerating: true,
            }
            setFilteredCanvases((prev) => [...prev, newCanvas])
        } else {
            setFilteredCanvases((prev) =>
                prev.filter((canvas) => canvas.canvasID !== 'Placeholder')
            )
        }
    }, [isPolling])

    const debounceFn = useCallback(
        debounce((value) => {
            setFilteredCanvases(
                canvases.filter((canvas) =>
                    canvas.canvasName.toLowerCase().includes(value.toLowerCase())
                )
            )
        }, 1000),
        [canvases]
    )

    useEffect(() => {
        const recipeMapping = localStorage.getItem('recipeMapping')
        const personal = workspacesAccessible.find((w) => w.workspaceID === personalWorkspace)
        if (workspacesAccessible.length > 0 && recipeMapping) {
            if (personal) {
                dispatch(switchWorkspace(personal.workspaceID, personal.name))
            } else {
                dispatch(
                    switchWorkspace(
                        workspacesAccessible[0].workspaceID,
                        workspacesAccessible[0].name
                    )
                )
            }
        }

        if (localStorage.getItem('lastCanvas')) {
            const lastCanvas = localStorage.getItem('lastCanvas')
            navigate(`${lastCanvas}`)
            localStorage.removeItem('lastCanvas')
        }
    }, [canvases, workspacesAccessible, personalWorkspace])

    const handleSearchChange = (value) => {
        setSearch(value)
        debounceFn(value)
    }

    const handleDeleteDialog = (canvasID) => {
        deleteModal.show({
            title: 'Canvas',
            type: 'Canvas',
            description: 'Are you sure you want to permanently delete this canvas',
            onDelete: () => deleteCanvas({ canvasID }),
        })
    }

    const checkCanvasCreation = () => {
        const maxAttempts = 40
        let attempts = 0
        if (cavasesAmountRef.current === null) cavasesAmountRef.current = canvases.length
        const intervalId = setInterval(async () => {
            attempts += 1
            try {
                const result = await refetch()
                const updatedCanvases = result.data
                if (cavasesAmountRef.current < updatedCanvases.length) {
                    clearInterval(intervalId)
                    dispatch(setPresentationLoading(false))
                    //Find the one with the greatest ID
                    const newCanvas = updatedCanvases.reduce((maxCanvas, canvas) => {
                        return Number(canvas.canvasID.slice(1)) >
                            Number(maxCanvas?.canvasID.slice(1) || -Infinity)
                            ? canvas
                            : maxCanvas
                    }, null)
                    navigate(`${newCanvas.canvasID}`)
                }
                if (attempts >= maxAttempts) {
                    clearInterval(intervalId)
                    dispatch(setPresentationLoading(false))
                    toast.error('Failed to create presentation. Please try again later.')
                }
            } catch (error) {
                console.error('Error fetching canvases:', error)
                clearInterval(intervalId)
            }
        }, 5000)
    }

    const isDefaultWorkspace = useIsTourWorkspace()

    const handleGoToDataset = () => {
        navigate(ROUTES.SOURCES, { state: { openNewDataset: true } })
    }

    const buttons = [
        { text: 'Go to Explorer', onClick: () => navigate(ROUTES.EXPLORER) },
        {
            text: 'Create Canvas from Recipe',
            onClick: () => dispatch(setShowRecipeModal(true)),
        },
    ]

    const showSearchAndButton =
        workspaceMetadataLoading || (workspaceMetadata?.inboxes.length > 0 && insights?.length > 0)

    return (
        <>
            {isDefaultWorkspace && (
                <div className={styles.demoBanner}>
                    <ScoopText className={styles.demoBannerText}>
                        This is a Demo Workspace with dummy data and limited functionality
                    </ScoopText>
                </div>
            )}
            <ScoopScreenLayout
                title={
                    <>
                        Canvases
                        <HelpBotPromptButton
                            prompt={HELP_BOT_PROMPTS.CANVAS_SCREEN}
                            variant="icon"
                            iconMinimal
                            tooltip="Design data stories on a collaborative canvas. Click to learn more."
                            tooltipPlacement="right"
                        />
                    </>
                }
                icon={<ProjectorScreenIcon size={24} className={styles.iconPrimary} />}
                withSearchBar={showSearchAndButton}
                searchPlaceholder="Search Canvases"
                searchValue={search}
                onSearchChange={handleSearchChange}
                headerActions={
                    showSearchAndButton &&
                    !isDefaultWorkspace && (
                        <div ref={anchorEl}>
                            <ScoopButton
                                variant="default"
                                size="sm"
                                leftSection={<PlusIcon size={18} weight="bold" />}
                                onClick={() => setOpenNewCanvas(true)}
                            >
                                New Canvas
                            </ScoopButton>
                        </div>
                    )
                }
                contentClassName={styles.container}
            >
                {isLoading || workspaceMetadataLoading ? (
                    <div className={styles.skeletonContainer}>
                        {Array.from({ length: 8 }, (_, i) => (
                            <ScoopSkeleton
                                key={i}
                                variant="rectangular"
                                width={290}
                                height={160}
                                className={styles.skeleton}
                            />
                        ))}
                    </div>
                ) : filteredCanvases?.length > 0 || insights?.length > 0 ? (
                    filteredCanvases.map((canvas) => (
                        <CanvasBox
                            key={canvas.canvasID}
                            canvasID={canvas.canvasID}
                            canvasName={canvas.canvasName}
                            thumbnailURL={canvas.thumbnailURL}
                            handleDeleteDialog={handleDeleteDialog}
                            isGenerating={canvas.isGenerating}
                            isTourWorkspace={isDefaultWorkspace}
                        />
                    ))
                ) : !workspaceMetadataLoading && workspaceMetadata?.inboxes.length === 0 ? (
                    <div className={styles.emptyContainer}>
                        <EmptyDataset
                            title="Welcome to Canvases"
                            description="Here you can create and organize your Canvases in Scoop. To get started, create your first dataset."
                            iconComponent={
                                <div className={styles.emptyIconWrapper}>
                                    <ProjectorScreenIcon
                                        size={32}
                                        weight="duotone"
                                        className={styles.iconDark}
                                    />
                                </div>
                            }
                            onClick={handleGoToDataset}
                        />
                    </div>
                ) : (
                    <div className={styles.emptyStateContainer}>
                        <ScoopColumn className={styles.emptyStateContent} align="center" gap="2xl">
                            <div className={styles.emptyIconWrapper}>
                                <ProjectorScreenIcon
                                    size={32}
                                    weight="duotone"
                                    className={styles.iconDark}
                                />
                            </div>
                            <ScoopColumn
                                className={styles.emptyStateTextContainer}
                                align="center"
                                gap="md"
                            >
                                <ScoopText className={styles.emptyStateTitle}>
                                    Welcome to Canvases
                                </ScoopText>
                                <ScoopText className={styles.emptyStateDescription}>
                                    To create a canvas, you'll need insights generated from Explorer
                                    or Process Analysis. Alternatively, you can jumpstart the
                                    process by using a pre-made recipe or leveraging AI generation.
                                </ScoopText>
                            </ScoopColumn>
                            <ScoopRow className={styles.emptyStateButtons} gap="lg">
                                {buttons.map((button, index) => (
                                    <ScoopButton key={index} size="xs" onClick={button.onClick}>
                                        {button.text}
                                    </ScoopButton>
                                ))}
                            </ScoopRow>
                        </ScoopColumn>
                    </div>
                )}
            </ScoopScreenLayout>
            <NewCanvasMenu
                open={openNewCanvas}
                startInterval={checkCanvasCreation}
                anchorEl={anchorEl.current}
                onClose={() => setOpenNewCanvas(false)}
                createdInbox={location.state?.createdInbox}
                mode={location.state?.mode}
            />
        </>
    )
}
